import { createEmptyState } from './initialData';
import type { DemoAction, DemoState } from './types';

const nextId = (
  prefix: string,
  collection: Record<string, unknown> | unknown[],
) => `${prefix}-${Object.keys(collection).length + 1}`;

const addActivity = (
  state: DemoState,
  title: string,
  type: string,
  targetId?: string,
) => ({
  ...state,
  activities: [
    {
      id: `activity-${state.activities.length + 1}`,
      title,
      type,
      targetId,
      actor: '张明',
      occurredAt: '刚刚',
    },
    ...state.activities,
  ],
});

export const demoReducer = (
  state: DemoState,
  action: DemoAction,
): DemoState => {
  switch (action.type) {
    case 'demo/reset':
      return createEmptyState();
    case 'demo/imported':
      return action.payload;
    case 'research/hydrated':
      return {
        ...state,
        topics: action.payload.topics,
        companies: action.payload.companies,
        materials: action.payload.materials,
        dictionaries: action.payload.dictionaries
          ? { ...state.dictionaries, ...action.payload.dictionaries }
          : state.dictionaries,
      };
    case 'dictionaries/synced':
      return {
        ...state,
        dictionaries: { ...state.dictionaries, ...action.payload },
      };
    case 'topic/created': {
      const id = nextId('topic', state.topics);
      const topic = {
        id,
        ...action.payload,
        directions: [],
        status: '研究中',
        attention: 3,
        updatedAt: '2026-08-10 14:30',
        currentConclusion: '待形成阶段结论',
        companyIds: [],
        materialIds: [],
        factIds: [],
        viewpointIds: [],
      };
      return addActivity(
        { ...state, topics: { ...state.topics, [id]: topic } },
        `创建专题《${topic.name}》`,
        '专题创建',
        id,
      );
    }
    case 'topic/archived':
      return {
        ...state,
        topics: {
          ...state.topics,
          [action.payload.topicId]: {
            ...state.topics[action.payload.topicId],
            status: '归档',
          },
        },
      };
    case 'topic/companyLinked': {
      const { topicId, companyId } = action.payload;
      const topic = state.topics[topicId];
      const company = state.companies[companyId];
      if (!topic || !company) return state;
      return addActivity(
        {
          ...state,
          topics: {
            ...state.topics,
            [topicId]: {
              ...topic,
              companyIds: Array.from(new Set([...topic.companyIds, companyId])),
            },
          },
          companies: {
            ...state.companies,
            [companyId]: {
              ...company,
              topicIds: Array.from(new Set([...company.topicIds, topicId])),
            },
          },
        },
        `将${company.shortName}加入专题`,
        '企业关联',
        companyId,
      );
    }
    case 'material/created': {
      const id = nextId('material', state.materials);
      const topicIds = action.payload.topicId ? [action.payload.topicId] : [];
      const material = {
        id,
        ...action.payload,
        topicIds,
        companyIds: [],
        publishedAt: '2026-08-10',
        updatedAt: '2026-08-10',
        favorite: false,
      };
      const topics = action.payload.topicId
        ? {
            ...state.topics,
            [action.payload.topicId]: {
              ...state.topics[action.payload.topicId],
              materialIds: [
                ...state.topics[action.payload.topicId].materialIds,
                id,
              ],
            },
          }
        : state.topics;
      return addActivity(
        { ...state, materials: { ...state.materials, [id]: material }, topics },
        `新增研究资料《${material.title}》`,
        '研究资料',
        id,
      );
    }
    case 'material/factExtracted': {
      const id = nextId('fact', state.facts);
      const fact = {
        id,
        ...action.payload,
        type: '市场',
        confidence: '高' as const,
        recordedAt: '2026-08-10',
      };
      const topic = state.topics[action.payload.topicId];
      return addActivity(
        {
          ...state,
          facts: { ...state.facts, [id]: fact },
          topics: {
            ...state.topics,
            [topic.id]: { ...topic, factIds: [...topic.factIds, id] },
          },
        },
        `从资料提取研究事实：${fact.content}`,
        '研究事实',
        id,
      );
    }
    case 'fact/viewpointCreated': {
      const id = nextId('viewpoint', state.viewpoints);
      const viewpoint = {
        id,
        ...action.payload,
        confidence: '高' as const,
        status: '持续验证',
        author: '张明',
        updatedAt: '2026-08-10',
      };
      const topic = state.topics[action.payload.topicId];
      return addActivity(
        {
          ...state,
          viewpoints: { ...state.viewpoints, [id]: viewpoint },
          topics: {
            ...state.topics,
            [topic.id]: { ...topic, viewpointIds: [...topic.viewpointIds, id] },
          },
        },
        `形成研究观点：${viewpoint.content}`,
        '研究观点',
        id,
      );
    }
    case 'viewpoint/conclusionCreated': {
      const conclusion = {
        id: `conclusion-${state.conclusions.length + 1}`,
        ...action.payload,
        version: `2026 Q${Math.min(4, state.conclusions.length + 2)}`,
        createdAt: '2026-08-10',
      };
      return addActivity(
        { ...state, conclusions: [conclusion, ...state.conclusions] },
        `形成阶段结论：${conclusion.summary}`,
        '阶段结论',
        conclusion.id,
      );
    }
    case 'company/created': {
      const id = nextId('company', state.companies);
      const company = {
        id,
        ...action.payload,
        shortName: action.payload.name.slice(0, 6),
        foundedAt: '2026-08-10',
        round: '未融资',
        valuation: '未披露',
        followStatus: '普通关注' as const,
        source: '手工创建',
        topicIds: [],
        latestEvent: '新建企业',
        judgment: '待研究',
      };
      return addActivity(
        { ...state, companies: { ...state.companies, [id]: company } },
        `新建企业${company.shortName}`,
        '企业',
        id,
      );
    }
    case 'company/followChanged': {
      const company = state.companies[action.payload.companyId];
      if (!company) return state;
      return addActivity(
        {
          ...state,
          companies: {
            ...state.companies,
            [company.id]: { ...company, followStatus: action.payload.status },
          },
        },
        `${company.shortName}关注状态更新为${action.payload.status}`,
        '企业关注',
        company.id,
      );
    }
    case 'company/projectLeadCreated': {
      const company = state.companies[action.payload.companyId];
      if (!company) return state;
      const id = nextId('lead', state.projectLeads);
      const topic = action.payload.topicId
        ? state.topics[action.payload.topicId]
        : undefined;
      const lead = {
        id,
        companyId: company.id,
        topicId: topic?.id,
        viewpointIds: topic?.viewpointIds || [],
        materialIds: topic?.materialIds || [],
        expertIds: ['expert-wang'],
        status: '线索' as const,
        createdAt: '2026-08-10',
      };
      return addActivity(
        {
          ...state,
          projectLeads: { ...state.projectLeads, [id]: lead },
          companies: {
            ...state.companies,
            [company.id]: {
              ...company,
              followStatus: '已转投资项目',
              investmentStatus: '线索',
            },
          },
        },
        `为${company.shortName}创建投资项目线索`,
        '项目线索',
        company.id,
      );
    }
    case 'institution/relationAdded': {
      const item = state.institutions[action.payload.institutionId];
      return item
        ? {
            ...state,
            institutions: {
              ...state.institutions,
              [item.id]: { ...item, relation: action.payload.relation },
            },
          }
        : state;
    }
    case 'expert/relationAdded': {
      const item = state.experts[action.payload.expertId];
      return item
        ? {
            ...state,
            experts: {
              ...state.experts,
              [item.id]: { ...item, relation: action.payload.relation },
            },
          }
        : state;
    }
    case 'expert/consultationRecorded': {
      const expert = state.experts[action.payload.expertId];
      if (!expert) return state;
      const usage = {
        id: `usage-${state.resourceUsages.length + 1}`,
        resourceId: expert.id,
        resourceType: 'expert' as const,
        companyId: action.payload.companyId,
        context: action.payload.summary,
        result: '有效支持项目判断',
        usedAt: '2026-08-10',
      };
      return addActivity(
        {
          ...state,
          experts: {
            ...state.experts,
            [expert.id]: {
              ...expert,
              collaborationCount: expert.collaborationCount + 1,
              lastCooperationAt: '2026-08-10',
            },
          },
          resourceUsages: [usage, ...state.resourceUsages],
        },
        `记录${expert.name}专家咨询`,
        '专家调用',
        expert.id,
      );
    }
    case 'service/relationAdded': {
      const item = state.services[action.payload.serviceId];
      return item
        ? {
            ...state,
            services: {
              ...state.services,
              [item.id]: { ...item, relation: action.payload.relation },
            },
          }
        : state;
    }
    case 'service/recommendationAdded': {
      const item = state.services[action.payload.serviceId];
      if (!item) return state;
      return addActivity(
        {
          ...state,
          services: {
            ...state.services,
            [item.id]: { ...item, recommendedCount: item.recommendedCount + 1 },
          },
        },
        `${item.name}推荐项目：${action.payload.companyName}`,
        'FA推荐',
        item.id,
      );
    }
    case 'investment/resultReturned': {
      const company = state.companies[action.payload.companyId];
      const viewpoint = state.viewpoints[action.payload.viewpointId];
      if (!company || !viewpoint) return state;
      const conclusion = {
        id: `conclusion-${state.conclusions.length + 1}`,
        topicId: viewpoint.topicId,
        version: '2026 Q3',
        summary: action.payload.result,
        viewpointIds: [viewpoint.id],
        createdAt: '2026-08-10',
      };
      return addActivity(
        {
          ...state,
          companies: {
            ...state.companies,
            [company.id]: { ...company, investmentStatus: '已投资' },
          },
          viewpoints: {
            ...state.viewpoints,
            [viewpoint.id]: {
              ...viewpoint,
              status: '已验证',
              updatedAt: '2026-08-10',
            },
          },
          conclusions: [conclusion, ...state.conclusions],
        },
        `${company.shortName}投资结果回流并验证研究观点`,
        '结果回流',
        company.id,
      );
    }
    case 'dictionary/itemAdded': {
      const list = state.dictionaries[action.payload.key];
      return {
        ...state,
        dictionaries: {
          ...state.dictionaries,
          [action.payload.key]: Array.from(
            new Set([...list, action.payload.value]),
          ),
        },
      };
    }
    case 'dictionary/itemUpdated': {
      const list = state.dictionaries[action.payload.key];
      const { from, to } = action.payload;
      if (!to.trim() || (to !== from && list.includes(to))) return state;
      return {
        ...state,
        dictionaries: {
          ...state.dictionaries,
          [action.payload.key]: list.map((item) => (item === from ? to : item)),
        },
      };
    }
    case 'dictionary/itemRemoved': {
      const list = state.dictionaries[action.payload.key];
      return {
        ...state,
        dictionaries: {
          ...state.dictionaries,
          [action.payload.key]: list.filter(
            (item) => item !== action.payload.value,
          ),
        },
      };
    }
    default:
      return state;
  }
};
